import { QueryClient } from '@tanstack/react-query'
import titleize from '@vangst/lib/titleize'
import { orderedJobPostingAtsStatuses } from '@vangst/services/oogst/company/statuses'
import useCompany, {
  optimisticDeleteCompany,
  prefetchCompany,
} from '@vangst/services/oogst/company/useCompany'
import { prefetchCompanyJobPostings } from '@vangst/services/oogst/company/useCompanyJobPostings'
import { fetchPage } from '@vangst/services/oogst/page'
import {
  JobPostingAtsStatusEnum,
  RobotsEnum,
} from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import React from 'react'
import Busy from '../../../../components/feedback/Busy'
import Breadcrumbs from '../../../../components/navigation/Breadcrumbs'
import Meta from '../../../../domains/application/Meta'
import CompanyJobPostingsRenderer from '../../../../domains/company/CompanyJobPostingsRenderer'
import CompanyRenderer from '../../../../domains/company/CompanyRenderer'
import PostAJob from '../../../../domains/company/PostAJob'
import SearchRenderer from '../../../../domains/search/SearchRenderer'
import CustomError from '../../../_error'

export async function getStaticPaths() {
  return { paths: [], fallback: true }
}

type StaticProps = GetStaticPropsContext<{ displayname?: string }>

export async function getStaticProps({ params }: StaticProps) {
  const queryClient = new QueryClient()
  const displayname = params?.displayname ? params.displayname : undefined
  const rawUrl = __filename
    .split('pages')[1]
    ?.replace('.js', '')
    .replace('[displayname]', displayname || '')

  const vars = displayname
    ? {
        atsFree: { displayname, first: 24 },
        company: { displayname },
        esVars: { displayname, first: 24 },
      }
    : undefined

  let company
  if (vars) {
    company = await prefetchCompany(queryClient, vars.company)
    await prefetchCompanyJobPostings(queryClient, vars.atsFree)
  }

  const is403 = company != null && company?.is403
  if (is403 && vars) optimisticDeleteCompany(queryClient, vars?.company)

  const page = await fetchPage(queryClient, { rawUrl })
  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    {
      href: Routes.COMPANIES_DETAIL.replace(
        ':displayname',
        displayname ?? '',
      ) as Routes,
      text: titleize(displayname || 'Company'),
    },
    { href: null, text: 'Jobs' },
  ]

  return {
    props: {
      displayname,
      is403,
      crumbs,
      page: page?.getPage ? page.getPage : null,
      vars,
    },
    notFound: company?.getClient == null && is403 === false,
    revalidate: 30,
  }
}

// -------------------------------------

type PropsType = InferGetStaticPropsType<typeof getStaticProps>

function BusyBusy() {
  return <Busy className="mt-12 flex items-center justify-center" />
}

const ThePage = React.memo(function ThePage(props: PropsType) {
  const { viewer } = useViewer()
  const { internalUser } = viewer?.permissions ?? {}
  const { crumbs, displayname, page, vars } = props
  if (!displayname || !vars) return <BusyBusy />

  return (
    <>
      <Meta
        key="meta"
        canonicalUrl={page?.metaInfo.canonicalUrl}
        crumbs={crumbs}
        description={`Find your dream job with ${titleize(
          displayname,
        )} today! Vangst.com is the cannabis industry's leading hiring platform.`}
        robots={page?.metaInfo.robots ?? RobotsEnum.IndexFollow}
        title={
          page?.metaInfo.title ??
          page?.title ??
          `${titleize(displayname)} Jobs on Vangst.com`
        }
      />
      <Breadcrumbs crumbs={crumbs} />
      <header className="contain-xyc mood-white mt-6 min-h-20 pb-2 sm:pb-2 md:flex md:items-center">
        <CompanyRenderer as="header" variables={vars.company} />
      </header>
      <article className="contain-xyc mood-white">
        <div className="mt-10 w-full">
          <div className="flex justify-end">
            <PostAJob slug={displayname} />
          </div>

          {internalUser &&
            Object.keys(orderedJobPostingAtsStatuses).map((stat) => (
              <SearchRenderer
                as="table-row"
                at="lanes"
                key={stat}
                className="mt-4"
                endpoint="searchJobPostingsByRecruiters"
                variables={{
                  ...vars.esVars,
                  atsStatus: stat as JobPostingAtsStatusEnum,
                }}
                label={
                  orderedJobPostingAtsStatuses[stat as JobPostingAtsStatusEnum]
                }
              />
            ))}

          {!internalUser && (
            <CompanyJobPostingsRenderer
              as="card"
              at="infinite"
              className="mt-4"
              classNameList="grid-four-up"
              variables={vars.atsFree}
            />
          )}
        </div>
      </article>
    </>
  )
})

function CompanyMight403(props: PropsType) {
  const { authenticated, authenticating, viewer } = useViewer()
  const vars = props.vars?.company ? props.vars.company : { displayname: '' }
  const { data, isLoading } = useCompany(vars, {
    enabled: !!(viewer != null && props.vars?.company),
  })
  const unauthorized =
    !authenticated || (isLoading === false && data?.getClient == null)

  if (authenticating || isLoading) return <BusyBusy />
  if (unauthorized) return <CustomError statusCode={403} />
  return <ThePage {...props} />
}

export default function CompaniesDetailPage(props: PropsType) {
  if (!props.displayname || !props.vars) return <BusyBusy />
  if (props.is403 === true) return <CompanyMight403 {...props} />
  return <ThePage {...props} />
}
